import { Navigation } from "@/services/Navigation";
import { RecommendationService } from "@/services/RecommendationService";
import { Analytics } from "@/services/Analytics";
import { useStore } from "@/store";
import { useActiveUser } from "@/store/selectors";
import {
  useHasRecommendedPlaylists,
  useRecommendedPlaylists,
} from "@/store/selectors/playlistSelectors";
import {
  useUserIndustries,
  useUserProfileData,
  useUserSubjects,
} from "@/store/selectors/userProfileSelectors";
import { Group, Space, Stack } from "@mantine/core";
import { IconRefresh, IconSearch } from "@tabler/icons-react";
import { useEffect } from "react";
import { Button } from "../ui/Button";
import { PlaylistCard } from "./PlaylistCard";
import { Text } from "../ui/Text";
import { AdvizerTag } from "../AdvizerTag";
import { UserProfileService } from "@/services/UserProfileService";

let totalRequests = 0;
let didRequestRecommendedPlaylists = false;

export const RecommendPlaylists = () => {
  const activeUser = useActiveUser();

  const userProfileData = useUserProfileData();
  const hasRecommendedPlaylists = useHasRecommendedPlaylists();
  const userIndustries = useUserIndustries();
  const userSubjects = useUserSubjects();

  const userCustomPlaylistRecommendationRequest = useStore(
    (state) => state.playlists.userCustomPlaylistRecommendationRequest,
  );
  const setExplicitPlaylistMessage = useStore(
    (state) => state.playlists.setExplicitPlaylistMessage,
  );
  const setShowPlaylistLoading = useStore(
    (state) => state.playlists.setShowPlaylistLoading,
  );
  // const [didRequestRecommendedPlaylists, setDidRequestRecommendedPlaylists] =
  //   useState(false);

  const didLoadUserData = useStore((state) => state.user.didLoadUserData);

  useEffect(() => {
    if (!hasRecommendedPlaylists) {
      setExplicitPlaylistMessage("Finding the best playlists for you...");
      setShowPlaylistLoading(true);
    } else {
      setExplicitPlaylistMessage("Here are some recommendations based on...");
      setShowPlaylistLoading(false);
    }

    return () => {
      setExplicitPlaylistMessage("");
      setShowPlaylistLoading(false);
    };
  }, [
    hasRecommendedPlaylists,
    setExplicitPlaylistMessage,
    setShowPlaylistLoading,
  ]);

  useEffect(() => {
    return () => {
      totalRequests = 0;
    };
  }, []);

  useEffect(() => {
    async function getRecommendedPlaylists() {
      totalRequests++;
      didRequestRecommendedPlaylists = true;

      await RecommendationService.getRecommendedPlaylists({
        userProfileData,
        customRequest: userCustomPlaylistRecommendationRequest,
        userId: activeUser?.id || "",
      });

      didRequestRecommendedPlaylists = false;
    }

    if (didRequestRecommendedPlaylists) return;
    if (!userProfileData) return;
    if (hasRecommendedPlaylists) return;
    if (totalRequests >= 5) return;
    if (activeUser && !didLoadUserData) return;

    getRecommendedPlaylists();
  }, [
    activeUser,
    didLoadUserData,
    hasRecommendedPlaylists,
    userCustomPlaylistRecommendationRequest,
    userProfileData,
  ]);

  return (
    <Stack maw={650} align="center">
      {/* <Text titleStyle="h6" ta="center" c="text.7">
        {`Recommendations are based on your interests in...`}
      </Text> */}

      {hasRecommendedPlaylists && (
        <Stack>
          <Group align="center" justify="center" gap="xs" maw={600}>
            {Object.values(userSubjects || {}).map((subject, index) => (
              <AdvizerTag
                key={subject.id}
                disableClick
                size="lg"
                tag={{
                  type: "subjects",
                  id: subject.id,
                  slug: subject.slug,
                  title: subject.title,
                }}
                index={index}
              />
            ))}
            {Object.values(userIndustries || {}).map((industry, index) => (
              <AdvizerTag
                key={industry.id}
                disableClick
                size="lg"
                tag={{
                  type: "industries",
                  id: industry.id,
                  slug: industry.slug,
                  title: industry.title,
                }}
                index={index}
              />
            ))}
          </Group>
          {userCustomPlaylistRecommendationRequest && (
            <Text c="text.7" ta="center">
              "{userCustomPlaylistRecommendationRequest}"
            </Text>
          )}
        </Stack>
      )}
      <Space h="sm" />
      {hasRecommendedPlaylists ? <ListOfRecommendedPlaylists /> : null}

      <Space h="xl" />
      {hasRecommendedPlaylists && (
        <Text size="xl  " ta="center">
          Don't see what you're looking for?
        </Text>
      )}
      {hasRecommendedPlaylists && (
        <Button
          radius="xl"
          leftSection={<IconRefresh size={18} />}
          to="/?step=select-subjects"
          onClick={() => {
            UserProfileService.clearSubjects();
            UserProfileService.clearIndustries();
          }}
          w={300}
          fullWidth
        >
          Start over
        </Button>
      )}

      {hasRecommendedPlaylists && (
        <Button
          radius="xl"
          leftSection={<IconSearch size={18} />}
          onClick={() => {
            Navigation.navToPlaylistLibrary();
            Analytics.click({
              action: "browse_all_playlists",
              label: "Browse all playlists",
              context: "recommended_playlists",
            });
          }}
          maw={300}
          fullWidth
        >
          Browse all playlists
        </Button>
      )}
    </Stack>
  );
};

export const ListOfRecommendedPlaylists = () => {
  const recommendedPlaylists = useRecommendedPlaylists();
  return (
    <Stack>
      {Object.keys(recommendedPlaylists)
        // .filter((playlistId) => playlistId !== activePlaylist?.id)
        .map((playlistId) => (
          <PlaylistCard
            key={playlistId}
            playlistId={playlistId}
            isPlaylistLibrary={true}
            advizerListHeight={120}
            hideRecommendedBanner={true}
          />
        ))}
    </Stack>
  );
};
